import { PropTypes } from 'prop-types';
import React  from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Input } from '@ge/components/input';
import { StatusColor } from '@ge/tokens/colors/colors';

const StyledInput = styled(Input)`
  ${({ errors, id }) => (errors[id] ? `border-color: ${StatusColor.DANGER}` : '')};
`;

export const NumericInputMetaField = React.memo(({
  defaultValue,
  label,
  metadata,
  minLength,
  maxLength,
  name,
  placeholder,
  pattern,
  tabIndex,
  className,
}) => {
  const { t } = useTranslation(['tasks']);
  const { readOnly, required } = metadata;
  const { control, errors } = useFormContext();

  const numericPattern = /^\d*\.?\d+$/; 

  const rules = {
        required: required,
        validate: (value) => {
          const trimmedValue = String(value ?? '').trim();

          if (required && trimmedValue.length === 0) {
            return t('form.required_field');
          }

          if (!required && trimmedValue.length === 0) {
            return true;
          }

          if (minLength && trimmedValue.length < minLength) {
            return t('form.invalid_value');
          }

          if (!numericPattern.test(trimmedValue)) {
            return t('form.invalid_number');
          }

          if (pattern && trimmedValue.length > 0) {
            try {
              const re = new RegExp(pattern);
              return re.test(trimmedValue) || t('form.invalid_value');
            } catch (e) {
              console.error(e);
            }
          }

          return true;
        },
      };

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      rules={rules}
      render={({ onChange, onBlur, value }) => {
        const currentValue = String(value ?? '');

        const handleChange = (e) => {
          let newValue = e.target.value;

          const liveNumericRegex = /^(\d*\.?\d*)$/;

          if (newValue === '' || liveNumericRegex.test(newValue)) {
            onChange(newValue);
          }
        };

        const handleBlur = (e) => {
         
          const cleanedValue = currentValue.replace(/[^0-9.]/g, ''); 
          
          const finalValue = cleanedValue.endsWith('.') ? cleanedValue.slice(0, -1) : cleanedValue;

          if (finalValue !== currentValue) {
            onChange(finalValue);
          }
          onBlur(e);
        };

        return (
          <StyledInput
            className={className}
            disabled={readOnly}
            errors={errors}
            id={name}
            label={label}
            minLength={minLength}
            maxLength={maxLength}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            tabIndex={tabIndex}
            type="text"
            inputMode="decimal"
            value={currentValue}
          />
        );
      }}
    />
  );
})

NumericInputMetaField.propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  metadata: PropTypes.instanceOf(Object).isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  pattern: PropTypes.string,
  tabIndex: PropTypes.number,
  className: PropTypes.string,
  pacRules: PropTypes.any,
};

NumericInputMetaField.defaultProps = {
  defaultValue: '',
  label: '',
  minLength: null,
  maxLength: null,
  placeholder: '',
  pattern: null,
  tabIndex: null,
  pacRules: null,
};